import axios from 'axios';

// Support both localhost development and production
// Using localhost for local testing
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000/';
// CloudFront distribution with HTTPS (commented out for local testing):
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://d102992ctl8xa5.cloudfront.net/';
// Previous EC2 direct URL (HTTP - commented out):
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://51.20.104.84:7000/';
// Previous Render URL (commented out):
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://chit-backend-v0.onrender.com/';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 900000, // 15 minutes for large file processing (allocation can take 12+ min)
});

// Request interceptor with ngrok support
api.interceptors.request.use(
  (config) => {
    // Add ngrok-skip-browser-warning header for ngrok tunnels
    if (config.baseURL?.includes('ngrok') || window.location.hostname.includes('ngrok')) {
      config.headers['ngrok-skip-browser-warning'] = 'true';
    }
    
    // Log only method and endpoint, not sensitive data
    // console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API functions
export const getStorageStats = async () => {
  const response = await api.get('/stats');
  return response.data;
};

export const listAllData = async () => {
  const response = await api.get('/data');
  return response.data;
};

export const encodeFile = async (file: File, dataName: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('data_name', dataName);
  
  const response = await api.post('/encode', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 900000, // 15 minutes for large file uploads (allocation can take 12+ min)
  });
  return response.data;
};

export const decodeFile = async (dataName: string, keyFile: File) => {
  const formData = new FormData();
  formData.append('keyFile', keyFile);
  
  const response = await api.post(`/decode/${dataName}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob',
    timeout: 900000, // 15 minutes for large file processing (allocation can take 12+ min)
  });
  
  // Ensure headers are accessible
  return {
    ...response,
    headers: response.headers || {}
  };
};

export const decodeFileByKey = async (keyFile: File) => {
  const formData = new FormData();
  formData.append('keyFile', keyFile);

  const response = await api.post('/decode', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob',
    timeout: 900000,
  });

  return {
    ...response,
    headers: response.headers || {}
  };
};

// Legacy decode function (for backward compatibility)
export const decodeFileLegacy = async (dataName: string) => {
  const response = await api.get(`/decode/${dataName}`, {
    responseType: 'blob',
  });
  return response;
};

export const getDataInfo = async (dataName: string) => {
  const response = await api.get(`/decode/${dataName}/info`);
  return response.data;
};

export const deleteData = async (dataName: string) => {
  const response = await api.delete(`/data/${dataName}`);
  return response.data;
};

export const healthCheck = async () => {
  const response = await api.get('/health');
  return response.data;
};

// ============================================================================
// ACCESS CONTROL API FUNCTIONS
// ============================================================================

// Types for access control
export interface CreateAccessTokenRequest {
  ttlMinutes?: number;
  permissions?: string[];
}

export interface CreateAccessTokenResponse {
  success: boolean;
  token: string;
  tokenId: string;
  expiresIn: number;
  message: string;
  accessUrl: string;
  stats: {
    totalTokens: number;
    activeTokens: number;
    expiredTokens: number;
    totalAccesses: number;
  };
}

export interface ValidateAccessResponse {
  success: boolean;
  isValid: boolean;
  isExpired: boolean;
  remainingTime: number;
  accessCount: number;
  message: string;
}

export interface AccessTokenInfo {
  tokenId: string;
  activatedAt?: number;
  expiresAt?: number;
  isActive: boolean;
  accessCount: number;
  lastAccessAt?: number;
}

export interface AccessStatsResponse {
  success: boolean;
  stats: {
    totalTokens: number;
    activeTokens: number;
    expiredTokens: number;
    totalAccesses: number;
  };
  message: string;
}

// Create access token (admin only)
export const createAccessToken = async (request: CreateAccessTokenRequest): Promise<CreateAccessTokenResponse> => {
  try {
    const response = await api.post('/admin/create-access-token', request, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 60000, // 60 seconds for token creation (Render.com free tier can be slow)
    });
    return response.data;
  } catch (error: any) {
    // Enhanced error handling
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      throw new Error('Request timed out. The server may be starting up. Please try again in a moment.');
    }
    if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment before trying again.');
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Access system with token
export const accessSystemWithToken = async (token: string): Promise<ValidateAccessResponse> => {
  const response = await api.get(`/access/${token}`, {
    timeout: 30000, // 30 seconds for system access
  });
  return response.data;
};

// Validate access token
export const validateAccessToken = async (token: string): Promise<ValidateAccessResponse> => {
  try {
    console.log('🔍 API: Validating token:', token.substring(0, 20) + '...');
    console.log('🔍 API: Making request to:', `${API_BASE_URL}validate-access/${token}`);
    
    const response = await api.get(`/validate-access/${token}`, {
      timeout: 30000, // 30 seconds for validation
    });
    
    console.log('✅ API: Token validation response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ API: Token validation failed:', error);
    console.error('❌ API: Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });
    throw error;
  }
};

// Get access token info
export const getAccessTokenInfo = async (token: string): Promise<{ success: boolean; info?: AccessTokenInfo; message: string }> => {
  const response = await api.get(`/access-info/${token}`, {
    timeout: 30000, // 30 seconds for info request
  });
  return response.data;
};

// Get access control statistics
export const getAccessStats = async (): Promise<AccessStatsResponse> => {
  try {
    const response = await api.get('/access-stats', {
      timeout: 60000, // 60 seconds for stats request (Render.com free tier can be slow)
    });
    return response.data;
  } catch (error: any) {
    // Enhanced error handling
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn('Stats request timed out - this is non-critical');
      // Return empty stats instead of throwing error
      return {
        success: true,
        message: 'Stats unavailable (request timed out)',
        stats: {
          totalTokens: 0,
          activeTokens: 0,
          expiredTokens: 0,
          totalAccesses: 0
        }
      };
    }
    throw error;
  }
};

export default api;
